import Immutable from "immutable";
import exactMath from "exact-math";

import {
  getStudentTotalCreditAmountToPay,
  getStudentSortByKey,
  getSchoolSortByKey,
  getStudentTotalCreditAmount
} from "state/school_money/selectors";

/**
 * @returns {Immutable.Set}
 */
export function getBasketItemsOfType(state, itemType) {
  return state.getIn(["school_money", "basketItems", itemType], Immutable.Map()).toSet();
}

/**
 * @returns {Immutable.Set}
 */
export function getBasketItems(state) {
  return state.getIn(["school_money", "basketItems"])
    .map(basketItemsByType => basketItemsByType.toSet())
    .flatten(1)
    .toSet();
}

/**
 * @returns {Immutable.Set}
 */
export function getSchoolBasketItems(state, schoolID) {
  const basketItems = getBasketItems(state);
  return basketItems.filter(basketItem => basketItem.get("schoolID") === schoolID);
}

/**
 * @returns {Immutable.Set}
 */
export function getStudentBasketItems(state, studentID) {
  const basketItems = getBasketItems(state);
  return basketItems.filter(basketItem => basketItem.get("studentID") === studentID);
}

/**
 * @returns {Immutable.List}
 */
export function getCollatedBasketItemList(state, studentID) {
  return state.getIn(["school_money", "basketItems"])
    .map(basketItems => (
      basketItems
        .filter(basketItem => basketItem.get("studentID") === studentID)
        .groupBy(basketItem => basketItem.get("groupID"))
    ))
    .flatten(1)
    .toList()
}

/**
 * basketItemGroup {
 *   type,
 *   schoolID,
 *   studentID,
 *   groupID,
 *   items: [ basketItem ],
 *   totalPrice
 * }
 *
 * @returns {Immutable.List}
 */
export function getBasketItemGroupList(state, studentID) {
  const basketItemGroups = getCollatedBasketItemList(state, studentID);
  return basketItemGroups
    .map(basketItemGroup => {
      const { type, groupID, schoolID, studentID } = basketItemGroup.first().toObject();
      const totalPrice = basketItemGroup
        .map((_, basketItemID) => getBasketItemPrice(state, type, basketItemID))
        .reduce((totalPrice, itemPrice) => exactMath.add(totalPrice, itemPrice), 0);
      return Immutable.Map({
        type,
        groupID,
        schoolID,
        studentID,
        items: basketItemGroup.toSet(),
        totalPrice
      });
    });
}

/**
 * @returns {Immutable.Map}
 */
export function getBasketItemByID(state, type, basketItemID) {
  return state.getIn(["school_money", "basketItems", type, basketItemID]);
}

/**
 * @returns {boolean}
 */
export function doesBasketItemExist(state, type, basketItemID) {
  return getBasketItemByID(state, type, basketItemID) !== undefined;
}

/**
 * @return {boolean}
 */
export function doesBasketItemGroupExist(state, type, groupID) {
  const basketItems = getBasketItemsOfType(state, type);
  return basketItems.some(basketItem => basketItem.get("groupID") === groupID);
}

/**
 * @returns {Immutable.Set}
 */
export function getStudentsHavingBasketItems(state, schoolID) {
  const basketItems = getBasketItems(state);
  return basketItems
    .filter(basketItem => basketItem.get("schoolID") === schoolID)
    .map(basketItem => basketItem.get("studentID"))
    .sortBy(studentID => getStudentSortByKey(state, studentID));
}

/**
 * @returns {Immutable.Set}
 */
export function getSchoolsHavingBasketItems(state) {
  const basketItems = getBasketItems(state);
  return basketItems
    .map(basketItem => basketItem.get("schoolID"))
    .sortBy(schoolID => getSchoolSortByKey(state, schoolID))
}

/**
 * @returns {boolean}
 */
export function isBasketEmpty(state) {
  const basketItems = getBasketItems(state);
  return basketItems.size === 0;
}

/**
 * @returns {number}
 */
export function getBasketTotalQuantity(state) {
  const basketItems = getBasketItems(state);
  return basketItems.toList()
    .map(basketItem => basketItem.get("quantity"))
    .reduce((totalQuantity, quantity) => totalQuantity + quantity, 0);
}

/**
 * @returns {number}
 */
export function getBasketItemPrice(state, type, basketItemID) {
  const basketItem = getBasketItemByID(state, type, basketItemID);
  return basketItem.get("price");
}

/**
 * Calculate total basket price for given student, not considering credit
 * @returns {number}
 */
function getStudentBasketTotalPrice(state, studentID) {
  const basketItems = getStudentBasketItems(state, studentID);
  return basketItems.toList()
    .map(basketItem => getBasketItemPrice(state, basketItem.get("type"), basketItem.get("basketItemID")))
    .reduce((totalPrice, itemPrice) => exactMath.add(totalPrice, itemPrice), 0);
}

/**
 * Calculate total basket price for given school, not considering credit
 * @returns {number}
 */
function getSchoolBasketTotalPrice(state, schoolID) {
  const studentIDs = getStudentsHavingBasketItems(state, schoolID);
  return studentIDs
    .toList()
    .map(studentID => getStudentBasketTotalPrice(state, studentID))
    .reduce((totalPrice, studentPrice) => exactMath.add(totalPrice, studentPrice), 0);
}

/**
 * Calculate total basket price for all schools, not considering credit
 * @returns {number}
 */
export function getBasketTotalPrice(state) {
  const schoolIDs = getSchoolsHavingBasketItems(state);
  return schoolIDs
    .toList()
    .map(schoolID => getSchoolBasketTotalPrice(state, schoolID))
    .reduce((totalPrice, schoolPrice) => exactMath.add(totalPrice, schoolPrice), 0);
}

/**
 * Calculate total price of the basket for a school, minus credit
 * @returns {number}
 */
export function getSchoolBasketTotalToPay(state, schoolID) {
  const totalPrice = getSchoolBasketTotalPrice(state, schoolID);
  const creditUsed = getCreditUsedInBasket(state, schoolID);
  // subtract each credit used amount from the total price
  const totalToPay = Object.values(creditUsed)
    .reduce((totalPrice, creditAmount) => exactMath.sub(totalPrice, creditAmount), totalPrice);
  return totalToPay;
}

/**
 * Calculate total basket price across all schools, minus credit
 * @returns {number}
 */
export function getBasketTotalToPay(state) {
  const schoolIDs = getSchoolsHavingBasketItems(state);
  return schoolIDs
    .map(schoolID => getSchoolBasketTotalToPay(state, schoolID))
    .reduce((totalPrice, schoolPrice) => exactMath.add(totalPrice, schoolPrice), 0);
}

/**
 * Get the credit used in the basket by the given student for the given payment type
 *
 * @param {string} paymentType "dinners" | "clubs"
 * @returns {number}
 */
function getCreditUsedInBasketForType(state, schoolID, basketItemType, creditType) {
  const studentIDs = getStudentsHavingBasketItems(state, schoolID);
  // get total price of basket items of the given type per student
  const basketPriceByStudent = Immutable.Map(studentIDs.map(studentID => {
    const basketPrice = getStudentBasketItems(state, studentID)
      .filter(basketItem => basketItem.get("type") === basketItemType)
      .map(basketItem => getBasketItemPrice(state, basketItem.get("type"), basketItem.get("basketItemID")))
      .reduce((totalPrice, itemPrice) => exactMath.add(totalPrice, itemPrice), 0);
    return [ studentID, basketPrice ];
  }));
  // get credit amount per student
  const creditByStudent = Immutable.Map(studentIDs.map(studentID => (
    [ studentID, getStudentTotalCreditAmountToPay(state, studentID, creditType) ]
  )));
  // calculate credit used per student
  const creditUsedByStudent = basketPriceByStudent.map((basketPrice, studentID) => {
    const studentCredit = creditByStudent.get(studentID);
    // used credit is basket price capped at available credit
    // treat negative credit amounts as 0
    return Math.min(basketPrice, Math.max(0, studentCredit));
  });
  // sum the student totals
  return creditUsedByStudent
    .reduce((totalAmount, studentAmount) => exactMath.add(totalAmount, studentAmount), 0);
}

/**
 * @returns {Object<string,number>} map of payment type to credit used
 */
export function getCreditUsedInBasket(state, schoolID) {
  return {
    dinners: getCreditUsedInBasketForType(state, schoolID, "dinnerBooking", "dinner_money"),
    clubs: getCreditUsedInBasketForType(state, schoolID, "clubBooking", "club_extended_services"),
    external_top_up: getCreditUsedInBasketForType(state, schoolID, "dinnerBooking", "external_top_up")
  }
}

/**
 * @returns {number}
 */
export function getCreditTopUpAmount(state, studentID, creditType) {
  const basketItems = getBasketItemsOfType(state, "creditTopUp");
  const basketItem = basketItems.find(basketItem => {
    const payload = basketItem.get("payload").toObject();
    return payload.studentID === studentID && payload.creditType === creditType;
  });
  if (basketItem === undefined) {
    return 0;
  }
  return basketItem.get("price");
}

/**
 * @returns {boolean}
 */
export function isSchoolBasketExpanded(state, schoolID) {
  return state.getIn(["school_money", "expandedSchoolBasketID"]) === schoolID;
}

/**
 * @returns {Immutable.List}
 */
export function getBasketItemsIssuesByItemType(state) {
  return state.getIn(["school_money", "basketItemsIssues"])
    .groupBy(issue => issue.get("itemType"));
}

/**
 * @returns {Immutable.List}
 */
export function getBasketItemsIssuesByReason(state) {
  return state.getIn(["school_money", "basketItemsIssues"])
    .groupBy(issue => issue.get("reason"));
}

/**
 * @returns {boolean}
 */
export function isBasketItemsValidating(state) {
  return state.getIn(["school_money", "basketItemsIssues"]) === null;
}

/**
 * @returns {boolean}
 */
export function isBasketItemsValid(state) {
  const isValidating = isBasketItemsValidating(state);
  const basketItemsIssues = state.getIn(["school_money", "basketItemsIssues"]);
  return !isValidating && basketItemsIssues.size === 0;
}

export function getBasketItemsIssuesByStudentAndReason(state) {
  return state.getIn(["school_money", "basketItemsIssues"])
    .groupBy(issue => {
      const { itemType, basketItemID } = issue.toObject();
      const basketItem = getBasketItemByID(state, itemType, basketItemID);
      return basketItem.get("studentID");
    })
    .map(group => group.groupBy(issue => issue.get("reason")));
}

/**
 * @returns {boolean}
 */
export function basketHasDinnerBookingWithArrear(state, schoolID) {
    const basketItems = getBasketItems(state);
    return basketItems
      .filter(basketItem => basketItem.get("schoolID") === schoolID)
      .filter(basketItem => basketItem.get("type") === "dinnerBooking")
      .filter(basketItem => getStudentTotalCreditAmount(state, basketItem.get("studentID"), "dinner_money") < 0)
      .toList().size > 0;
}



// WEBPACK FOOTER //
// ./src/state/school_money/selectors/basket.js